import TableHead from './TableHead';
import TableFooter from './TableFooter';
import { FixedSizeList } from 'react-window';
import TableRow from './TableRow';
import AutoSizer from 'react-virtualized-auto-sizer';
import cn from 'classnames';
import React, { useContext, useMemo } from 'react';

interface OuterElementContextValue {
    headHeight: number;
    stickyRowsCount: number;
    rowHeight: number;
    contentWidthPx?: number;
    isLoading?: boolean;
    footerHeight: number;
    tableChildren?: React.ReactNode;
}

const OuterElementContext = React.createContext<OuterElementContextValue>({
    headHeight: 38,
    stickyRowsCount: 0,
    rowHeight: 38,
    footerHeight: 0,
});

const OuterElement = React.forwardRef<HTMLDivElement, any>(
    ({ children, ...props }, ref) => {
        const {
            headHeight,
            stickyRowsCount,
            rowHeight,
            contentWidthPx,
            isLoading,
            footerHeight,
            tableChildren,
        } = useContext(OuterElementContext);
        const scaledStickyRowHeight = rowHeight;

        return (
            <div {...props} ref={ref}>
                <div style={{ position: 'sticky', top: 0, zIndex: 100, height: headHeight }}>
                    <TableHead
                        height={headHeight}
                        style={{
                            minWidth: contentWidthPx ? `${contentWidthPx}px` : undefined,
                        }}
                    />
                </div>
                {stickyRowsCount > 0 && (
                    new Array(stickyRowsCount).fill(0).map((_, index) => (
                        <div
                            key={`stickyRow${index}`}
                            style={{
                                position: 'sticky',
                                top: headHeight + (index * scaledStickyRowHeight),
                                zIndex: 1,
                                height: scaledStickyRowHeight
                            }}
                        >
                            <TableRow
                                isSticky
                                index={index}
                                isLoading={isLoading}
                                style={{
                                    minWidth: contentWidthPx ? `${contentWidthPx}px` : undefined,
                                }}
                            />
                        </div>
                    ))
                )}
                {children}
                {footerHeight > 0 && (
                    <div style={{ position: 'sticky', bottom: 0, zIndex: 100, height: footerHeight }}>
                        <TableFooter
                            height={footerHeight}
                            style={{
                                minWidth: contentWidthPx ? `${contentWidthPx}px` : undefined,
                            }}
                        />
                    </div>
                )}
                {tableChildren}
            </div>
        );
    }
);

interface VirtualizedTableProps {
    isLoading?: boolean;
    headHeight?: number;
    rowHeight?: number;
    footerHeight?: number;
    rowsCount: number;
    stickyRowsCount?: number;
    contentWidthPx?: number;
    useContentHeight?: boolean;
    onRowHover?: (index: number | null) => void;
    showFallback?: boolean;
    style?: React.CSSProperties;
    children?: React.ReactNode;
    testId?: string;
    className?: string;
}

const VirtualizedTable: React.FC<VirtualizedTableProps> = ({
    isLoading,
    headHeight = 38,
    rowHeight = 38,
    footerHeight = 0,
    rowsCount,
    stickyRowsCount = 0,
    style,
    children: tableChildren,
    testId,
    className,
    contentWidthPx,
    onRowHover,
    useContentHeight = false,
    showFallback,
}) => {
    const contentHeight = useMemo(() => {
        const totalRowsHeight = rowsCount * rowHeight;
        return headHeight + totalRowsHeight + footerHeight;
    }, [headHeight, rowHeight, rowsCount, footerHeight]);

    const defaultFallback = useMemo(
        () => {
            if (rowsCount === 0 && !isLoading && showFallback) {
                return (
                    <span className="Table__fallback">No matching items found</span>
                );
            }

            return null;
        },
        [rowsCount, isLoading, showFallback]
    );

    const outerElementContextValue = useMemo<OuterElementContextValue>(() => ({
        headHeight,
        stickyRowsCount,
        rowHeight,
        contentWidthPx,
        isLoading,
        footerHeight,
        tableChildren,
    }), [headHeight, stickyRowsCount, rowHeight, contentWidthPx, isLoading, footerHeight, tableChildren]);

    return (
        <div
            className={cn('Table', className)}
            style={{
                height: '100%',
                ...style,
            }}
            data-testid={testId}
        >
            <div style={{ flex: 1, position: 'relative', maxHeight: useContentHeight ? contentHeight : undefined }}>
                <OuterElementContext.Provider value={outerElementContextValue}>
                    <AutoSizer>
                        {({ width, height }: { width: number; height: number }) => {
                            const effectiveHeight = Math.min(contentHeight, height);
                            return (
                                <div style={{ position: 'relative', width, height: effectiveHeight }}>
                                    <FixedSizeList
                                        itemSize={rowHeight}
                                        width={width}
                                        height={effectiveHeight}
                                        itemCount={rowsCount - stickyRowsCount}
                                        outerElementType={OuterElement}
                                    >
                                        {({ index, style: itemStyle }) => (
                                            <TableRow
                                                index={index + stickyRowsCount}
                                                isLoading={isLoading}
                                                onHover={onRowHover}
                                                style={{
                                                    ...itemStyle,
                                                    top: (itemStyle.top as number) + headHeight + (stickyRowsCount * rowHeight),
                                                    minWidth: contentWidthPx ? `${contentWidthPx}px` : undefined,
                                                }}
                                            />
                                        )}
                                    </FixedSizeList>
                                    {defaultFallback}
                                </div>
                            );
                        }}
                    </AutoSizer>
                </OuterElementContext.Provider>
            </div>
        </div>
    );
};

export default VirtualizedTable;
